import { useEffect, useState } from 'react';
import { Card, Table, Badge, Alert, Spinner, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faClock,
  faMapPin,
  faPoundSign,
  faUsers,
  faUserMd,
  faTimes,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import ShiftApplicantsModal from 'components/modals/ShiftApplicantsModal';
import ApplicantDetailModal from 'components/modals/ApplicantDetailModal';
import { getMyShifts } from 'features/shift/shiftApi';

interface Shift {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  address?: string;
  pay_rate: number;
  locum_role: string;
  booked_locums_count: number;
  required_locums: number;
  status: string;
}

const PharmacistBookingsApplicantsPage = () => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Shift Applicants Modal State
  const [showShiftApplicants, setShowShiftApplicants] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);

  // Applicant Detail Modal State
  const [showApplicantDetail, setShowApplicantDetail] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null
  );

  const fetchShifts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMyShifts();
      setShifts(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  const handleViewApplicants = (shift: Shift) => {
    setSelectedShiftId(shift.id);
    setSelectedShift(shift);
    setShowShiftApplicants(true);
  };

  const handleViewApplicantDetail = (bookingId: number) => {
    setSelectedBookingId(bookingId);
    setShowShiftApplicants(false);
    setShowApplicantDetail(true);
  };

  const handleBackToApplicants = () => {
    setShowApplicantDetail(false);
    setShowShiftApplicants(true);
  };

  return (
    <Container className="container py-2 py-sm-3">
      {/* Page Header */}
      <div className="mb-4">
        <p className="text-body-tertiary text-uppercase fs-10 fw-bold mb-1">
          Pharmacy Workspace
        </p>
        <h2 className="text-body-emphasis mb-1">Booking Applicants</h2>
        <p className="mb-0 text-body-tertiary fs-9">
          View applicants for your posted shifts and review their profiles.
        </p>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          {error}
        </Alert>
      )}

      {loading ? (
        <Card className="border border-translucent shadow-sm">
          <Card.Body className="text-center py-6">
            <Spinner animation="border" role="status" className="text-primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border border-translucent shadow-sm">
          <Card.Header className="bg-body-emphasis d-flex justify-content-between align-items-center">
            <Card.Title className="mb-0 fs-8 text-body-emphasis fw-bold">
              Your Shifts with Applicants
            </Card.Title>
            <small className="text-body-tertiary">Total: {shifts.length}</small>
          </Card.Header>

          <Card.Body className="p-3 ">
            {shifts.length === 0 ? (
              <div className="text-center py-6 px-4">
                <p className="text-body-secondary mb-2 fw-semibold">
                  No shifts posted yet
                </p>
                <p className="text-body-tertiary fs-9 mb-0">
                  Create a shift to start receiving applicants.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="phoenix-table mb-0">
                  <thead className="border-bottom border-translucent">
                    <tr>
                      <th style={{ width: '120px' }} className="fw-bold text-uppercase fs-9">
                        Date
                      </th>

                      <th style={{ width: '140px' }} className="fw-bold text-uppercase fs-9">
                        Time
                      </th>

                      <th style={{ minWidth: '250px' }} className="fw-bold text-uppercase fs-9">
                        Location
                      </th>

                      <th style={{ width: '180px' }} className="fw-bold text-uppercase fs-9">
                        Role
                      </th>

                      <th style={{ width: '120px' }} className="fw-bold text-uppercase fs-9">
                        Pay Rate
                      </th>

                      <th style={{ width: '120px' }} className="fw-bold text-uppercase fs-9">
                        Applicants
                      </th>

                      <th style={{ width: '120px' }} className="fw-bold text-uppercase fs-9 text-center">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {shifts.map(shift => (
                      <tr key={shift.id} className="align-middle">
                        {/* Date */}
                        <td>
                          <FontAwesomeIcon
                            icon={faCalendar}
                            className="me-2 text-primary"
                          />
                          <span className="fw-semibold">
                            {new Date(shift.date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit'
                            })}
                          </span>
                        </td>

                        {/* Time */}
                        <td>
                          <FontAwesomeIcon
                            icon={faClock}
                            className="me-2 text-info"
                          />
                          <span>
                            {shift.start_time.slice(0, 5)} -{' '}
                            {shift.end_time.slice(0, 5)}
                          </span>
                        </td>

                        {/* Location */}
                        <td>
                          <FontAwesomeIcon
                            icon={faMapPin}
                            className="me-2 text-danger"
                          />
                          <span className="text-body-emphasis">
                            {shift.location}
                          </span>
                          {shift.address && (
                            <div>
                              <small className="text-body-secondary">
                                {shift.address}
                              </small>
                            </div>
                          )}
                        </td>

                        {/* Role */}
                        <td>
                          <FontAwesomeIcon
                            icon={faUserMd}
                            className="me-2 text-success"
                          />
                          <Badge
                            bg="body-secondary"
                            className="text-body-emphasis"
                          >
                            {shift.locum_role}
                          </Badge>
                        </td>

                        {/* Pay Rate */}
                        <td>
                          <FontAwesomeIcon
                            icon={faPoundSign}
                            className="me-2"
                          />
                          <span className="fw-semibold">{shift.pay_rate}</span>
                        </td>

                        {/* Applicants Count */}
                        <td>
                          <Badge bg="primary" className="fs-9">
                            <FontAwesomeIcon icon={faUsers} className="me-1" />
                            {shift.booked_locums_count} /{' '}
                            {shift.required_locums}
                          </Badge>
                        </td>

                        {/* Action */}
                        <td className="text-start">
                          {shift.booked_locums_count > 0 ? (
                            <Button
                              size="sm"
                              variant="phoenix-primary"
                              onClick={() => handleViewApplicants(shift)}
                            >
                              <FontAwesomeIcon icon={faEye} className="me-1" />
                              View
                            </Button>
                          ) : (
                            <span className="text-body-secondary fs-9">
                              No applicants
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Shift Applicants Modal */}
      <ShiftApplicantsModal
        show={showShiftApplicants}
        shiftId={selectedShiftId}
        shiftData={selectedShift}
        onHide={() => setShowShiftApplicants(false)}
        onViewApplicant={handleViewApplicantDetail}
      />

      {/* Applicant Detail Modal */}
      <ApplicantDetailModal
        show={showApplicantDetail}
        bookingId={selectedBookingId}
        onHide={() => {
          setShowApplicantDetail(false);
          handleBackToApplicants();
        }}
      />
    </Container>
  );
};

export default PharmacistBookingsApplicantsPage;
